import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import UnauthorizedLayout from '../UnauthorizedLayout'
import AuthService from '../../../services/AuthService'
import FormInput from '../../ui/FormInput'
import FormButton from '../../ui/FormButton'
import useNotification from '../../../hooks/useNotification'

const ResetPassword = () => {
  const { t } = useTranslation('auth')
  const effectRan = useRef(false)

  const { token } = useParams()

  const [loading, setLoading] = useState(true)

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  })

  const { info, error: showError } = useNotification()

  const navigate = useNavigate()

  const onSubmit = async (data) => {
    try {
      await AuthService.resetPassword(token, data.password)
      // messaggio tradotto: la risposta del backend è un oggetto/testo inglese
      info(t('resetPassword.toast.success'))
      navigate('/login')
    } catch (e) {
      console.error('Reset password failed:', e?.response?.data)
      const detail = e?.response?.data?.error || e?.response?.data?.detail
      showError(
        typeof detail === 'string' ? detail : t('resetPassword.toast.error')
      )
      reset()
    }
  }

  const backToLogin = () => {
    navigate('/login')
  }

  useEffect(() => {
    if (effectRan.current === false) {
      AuthService.verifyResetToken(token)
        .then((response) => {
          console.log(response.data)
        })
        .catch((e) => {
          console.error('Reset password link is not valid:', e?.response?.data)
          showError(
            e?.response?.data?.message || t('confirmPassword.toast.invalidLink')
          )
          navigate('/login')
        })
        .finally(() => setLoading(false))
      return () => {
        effectRan.current = true
      }
    }
  }, [])

  if (loading) return <></>

  return (
    <UnauthorizedLayout
      title={t('resetPassword.title')}
      subtitle={t('resetPassword.subtitle')}
      question={t('resetPassword.question')}
      text={t('resetPassword.backLink')}
      hancleClick={backToLogin}
    >
      <div className="w-[100%] desktop:w-[500px] flex flex-col gap-4">
        <form
          className="mt-6 flex flex-col gap-2"
          onSubmit={handleSubmit(onSubmit)}
        >
          <FormInput
            label={t('labels.password')}
            name="password"
            placeholder={t('resetPassword.placeholder.password')}
            register={register}
            validationRules={{
              required: t('validation.passwordRequired'),
              pattern: {
                value: /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{10,256}/,
                message: t('resetPassword.validation.passwordNotSecure'),
              },
            }}
            errors={errors}
            withPasswordToggle={true}
          />
          <FormInput
            label={t('labels.confirmPassword')}
            name="confirmPassword"
            type="password"
            placeholder={t('resetPassword.placeholder.confirmPassword')}
            register={register}
            validationRules={{
              required: t('validation.passwordRequired'),
              validate: (value) =>
                value === getValues('password') ||
                t('validation.passwordsNotMatch'),
            }}
            errors={errors}
          />
          <div className="mt-3">
            <FormButton
              isSubmitting={isSubmitting}
              text={t('resetPassword.button')}
            />
          </div>
        </form>
      </div>
    </UnauthorizedLayout>
  )
}

export default ResetPassword
